import * as React from "react";
import { CalendarPlus2, Quote, Undo2 } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import TextareaAutosize from "react-textarea-autosize";
import { ChronologyListRecordDate } from "./chronologyListRecordDate";
import { ChronoEntryFormInputs } from "./types";
import { type ChronoEntry, type DocumentChronology } from "@/types/chronology";
import { Button } from "@/components/ui/button";
import { useCurrentPdf } from "@/views/chat/hooks/useCurrentPdf";
import { CitationInfo } from "@/views/chat/types";
import { useToggle } from "react-use";
import { ChronologyListRecordDelete } from "@/views/chronology/components/chronologyList/chronologyListRecordDelete";
import { ChronologyListRecordLabels } from "@/views/chronology/components/chronologyList/chronologyListRecordLabels";
import { ChronologyListRecordSave } from "@/views/chronology/components/chronologyList/chronologyListRecordSave";
import PencilIcon from "@heroicons/react/24/outline/PencilIcon";
import { MyCalendarCreateEventDialog } from "@/modules/myCalendar/myCalendarCreateEventDialog";

interface Props {
  chronoRecord?: ChronoEntry;
  index: number;
  initIsEditable?: boolean;
  documentChronology: DocumentChronology;
  onSave?: () => void;
  documentId: string;
}

export const ChronologyListRecord: React.FC<Props> = ({
  onSave,
  documentChronology,
  chronoRecord,
  index,
  initIsEditable,
  documentId,
}) => {
  const methods = useForm<ChronoEntryFormInputs>({
    defaultValues: {
      reworded_quote: chronoRecord?.reworded_quote || chronoRecord?.quote || "",
    },
  });
  const { handleCitationClick } = useCurrentPdf();
  const [isEditable, toggleIsEditable] = useToggle(!!initIsEditable);

  const [chronoEntryBaseEvent, setChronoEntryBaseEvent] = React.useState<ChronoEntry | null>(null);

  const handleHighlightClick = () => {
    if (!chronoRecord) {
      return;
    }

    const highlight = chronoRecord.highlights[0];
    if (!highlight) return;

    const citationInfo: CitationInfo = {
      citationId: String(index),
      page: highlight.page,
      boundingRect: {
        left: highlight.min_x * 100,
        top: highlight.min_y * 100,
        width: (highlight.max_x - highlight.min_x) * 100,
        height: (highlight.max_y - highlight.min_y) * 100,
      },
      fseId: chronoRecord.fse_id,
      documentId: "",
      citation: 0,
      index,
    };
    handleCitationClick(citationInfo);
  };

  const handleCreateOutlookEventClick = async (chronoRecord: ChronoEntry | undefined) => {
    if (!chronoRecord) {
      return;
    }

    if (chronoEntryBaseEvent?.id === chronoRecord.id) {
      setChronoEntryBaseEvent(null);
    } else {
      setChronoEntryBaseEvent(chronoRecord);
    }
  };

  const year =
    chronoRecord && chronoRecord.year && chronoRecord?.year !== -1 ? chronoRecord.year : chronoRecord?.nlp_year;
  const day = chronoRecord && chronoRecord.day && chronoRecord?.day !== -1 ? chronoRecord.day : chronoRecord?.nlp_day;

  return (
    <tr>
      <FormProvider {...methods}>
        <ChronologyListRecordDate chronoRecord={chronoRecord} isEditable={isEditable} />
        <td className="px-3 py-3" valign="top">
          {isEditable ? (
            <TextareaAutosize
              {...methods.register(`reworded_quote`)}
              className="w-full appearance-none rounded border border-transparent bg-slate-100 text-sm text-slate-600 outline-none hover:border-slate-200 focus:border-indigo-400 focus:bg-white focus:ring-2 focus:ring-indigo-100"
            />
          ) : (
            <div className="mb-1 text-sm text-gray-900">{chronoRecord?.reworded_quote || chronoRecord?.quote}</div>
          )}
          {chronoRecord && <ChronologyListRecordLabels chronoRecord={chronoRecord} />}
        </td>

        <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6" valign="top">
          {!isEditable && !!chronoRecord?.highlights?.length && (
            <Button
              variant="secondary"
              size="sm"
              className="mr-1 w-full text-xs sm:w-auto"
              onClick={handleHighlightClick}
            >
              <Quote className="mr-1 h-3 w-3" />
            </Button>
          )}

          {!!chronoRecord && (
            <Button variant="secondary" size="sm" className="mr-1 w-full text-xs sm:w-auto" onClick={toggleIsEditable}>
              {isEditable ? <Undo2 className="mr-1 h-3 w-3" /> : <PencilIcon className="mr-1 h-3 w-3" />}
            </Button>
          )}

          {isEditable && (
            <ChronologyListRecordSave
              toggleIsEditable={toggleIsEditable}
              chronoRecord={chronoRecord}
              onSave={onSave}
              documentChronology={documentChronology}
            />
          )}
          {!isEditable && chronoRecord && <ChronologyListRecordDelete id={chronoRecord.id} />}

          {!isEditable && (
            <Button
              variant="secondary"
              size="sm"
              disabled={!year || !day}
              className="mx-1 w-full text-xs sm:w-auto"
              onClick={() => handleCreateOutlookEventClick(chronoRecord)}
            >
              <CalendarPlus2 className="mr-1 h-3 w-3" />
            </Button>
          )}
        </td>
      </FormProvider>

      {chronoEntryBaseEvent !== null && (
        <MyCalendarCreateEventDialog
          onClose={() => setChronoEntryBaseEvent(null)}
          documentId={documentId}
          chronoEntryBaseEvent={chronoEntryBaseEvent}
        />
      )}
    </tr>
  );
};
